import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Plus, Edit, Trash2, MapPin, Clock, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_CATEGORIES = ['General', 'Worship', 'Community', 'Youth', 'Children', 'Outreach', 'Training', 'Special'];

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    image: '',
    category: 'General',
    max_capacity: '',
    registration_deadline: '',
    is_visible: true
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null
      };
      
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, data);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, data);
        toast.success('Event created');
      }
      setShowDialog(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      image: event.image || '',
      category: event.category || 'General',
      max_capacity: event.max_capacity || '',
      registration_deadline: event.registration_deadline || '',
      is_visible: event.is_visible !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      image: '',
      category: 'General',
      max_capacity: '',
      registration_deadline: '',
      is_visible: true
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Worship': 'bg-purple/10 text-purple',
      'Community': 'bg-gold/10 text-gold',
      'Youth': 'bg-blue-100 text-blue-600',
      'Children': 'bg-pink-100 text-pink-600',
      'Outreach': 'bg-olive/10 text-olive',
      'Training': 'bg-orange-100 text-orange-600',
      'Special': 'bg-burgundy/10 text-burgundy'
    };
    return colors[category] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-events-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Events</h1>
          <p className="text-charcoal/60">Manage church events</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-event-btn"
        >
          <Plus size={18} className="mr-2" /> Add Event
        </Button>
      </div>

      <div className="grid gap-4">
        {events.map((event) => (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                {event.image ? (
                  <img src={event.image} alt={event.title} className="w-20 h-20 object-cover rounded-xl" />
                ) : (
                  <div className="w-20 h-20 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar size={32} className="text-gold" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-serif text-lg text-charcoal">{event.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(event.category)}`}>
                      {event.category}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-charcoal/60">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {event.date}
                    </span>
                    {event.time && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} /> {event.time}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {event.location}
                      </span>
                    )}
                    {event.max_capacity && (
                      <span className="flex items-center gap-1">
                        <Users size={14} /> Max: {event.max_capacity}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(event)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(event.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
        {events.length === 0 && (
          <div className="text-center py-12 text-charcoal/50">
            <Calendar size={48} className="mx-auto mb-4 opacity-20" />
            <p>No events yet. Create your first event!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingEvent ? 'Edit Event' : 'Create Event'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Capacity</Label>
                <Input
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({...formData, max_capacity: e.target.value})}
                  placeholder="Leave empty for unlimited"
                />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Event location"
              />
            </div>
            <div>
              <Label>Registration Deadline</Label>
              <Input
                type="date"
                value={formData.registration_deadline}
                onChange={(e) => setFormData({...formData, registration_deadline: e.target.value})}
              />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({...formData, image: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Event description..."
                height="150px"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={formData.is_visible}
                onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
              />
              <Label htmlFor="is_visible">Visible on website</Label>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
